import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Plus, Minus, Trash2, CreditCard, Banknote, ShoppingCart,
  Smartphone, Receipt, Percent, X, Clock, ArrowLeft,
  UtensilsCrossed, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useCartStore from '../store/cartStore';
import useOfflineStore from '../store/offlineStore';
import ModifierSelection from '../components/ModifierSelection';
import OrderReceiptModal from '../components/OrderReceiptModal';
import UiButton from '../components/ui/UiButton';
import UiDateField from '../components/ui/UiDateField';
import UiEmptyState from '../components/ui/UiEmptyState';
import UiFilterChip from '../components/ui/UiFilterChip';
import UiModalShell from '../components/ui/UiModalShell';
import UiPanel from '../components/ui/UiPanel';
import UiSearchField from '../components/ui/UiSearchField';
import UiTimeField from '../components/ui/UiTimeField';
import { formatUiCurrencyDetailed, formatUiDateTime } from '../utils/uiFormat';
import { toIsoDateString } from '../utils/dateFormat';

const POS = () => {
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  // Forced till cash-count (Settings > cash_count_interval_minutes; 0 = off)
  const [lastCashCountAt, setLastCashCountAt] = useState(null);
  const [showCashCountModal, setShowCashCountModal] = useState(false);
  const [cashCountAmount, setCashCountAmount] = useState('');
  const [savingCashCount, setSavingCashCount] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [discountNote, setDiscountNote] = useState('');
  const [showMobileCart, setShowMobileCart] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [showModifierSelection, setShowModifierSelection] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [showRecentSales, setShowRecentSales] = useState(false);
  const [recentSales, setRecentSales] = useState([]);
  const [recentSalesLoading, setRecentSalesLoading] = useState(false);
  const [historicalOrder, setHistoricalOrder] = useState(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Backdate mode — activated via ?mode=backdate from Orders page
  const isBackdateMode = searchParams.get('mode') === 'backdate';
  const todayIso = toIsoDateString(new Date());
  const [backdateDate, setBackdateDate] = useState(() => localStorage.getItem('pos_backdate_date') || '');
  const [backdateTime, setBackdateTime] = useState(() => localStorage.getItem('pos_backdate_time') || '12:00');
  const [backdateSaleCount, setBackdateSaleCount] = useState(0);
  const [addedBackdateSales, setAddedBackdateSales] = useState([]);
  const {
    items, addItem, removeItem, updateQuantity,
    getSubtotal, getDiscountValue, getTotal, getItemCount,
    clearCart, setDiscount, setNotes, getOrderData, discountPercent, notes,
    getItemDisplayName
  } = useCartStore();

  const {
    isOnline, addPendingOrder,
    cacheMenuItems, cacheCategories, cacheSettings,
    cachedMenuItems, cachedCategories, cachedSettings
  } = useOfflineStore();

  useEffect(() => {
    fetchData();
  }, []);

  // Load pending order into cart for editing (from Orders page)
  useEffect(() => {
    const editOrderId = searchParams.get('edit_order');
    if (editOrderId && menuItems.length > 0) {
      const loadOrder = async () => {
        try {
          const res = await api.get(`/orders/${editOrderId}/`);
          const order = res.data;
          if (order.status !== 'pending') {
            toast.error('Only pending orders can be edited in POS');
            return;
          }
          clearCart();
          (order.items || []).forEach(item => {
            const menuItem = menuItems.find(m => m.id === item.menu_item);
            if (menuItem) {
              const menuItemModifiers = menuItem.modifiers || [];
              const mappedSelections = (item.modifier_selections || []).map(selection => ({
                groupId: menuItemModifiers.find(link => (link.modifier_detail?.options || []).some(option => option.id === selection.modifier_option))?.modifier,
                modifier_option_id: selection.modifier_option,
                name: selection.modifier_option_name,
                raw_material_id: selection.raw_material,
                raw_material_name: selection.raw_material_name,
                unit_name: selection.unit_name,
                quantity: selection.quantity,
                quantity_used: parseFloat(selection.quantity_used || 0),
                price_adjustment: parseFloat(selection.price_at_time || 0),
              }));
              const mappedBaseComponents = item.base_components_snapshot || menuItem.base_components || [];
              const mappedPortion = getPortionConfigForItem(menuItem, item.menu_item_portion, item.portion_name);
              addItem(
                { ...menuItem, selling_price: parseFloat(item.unit_price) },
                mappedSelections,
                (item.modifier_selections || []).reduce((sum, selection) => sum + (parseFloat(selection.price_at_time || 0) * parseInt(selection.quantity || 1, 10)), 0),
                mappedBaseComponents,
                mappedPortion
                  ? {
                      id: mappedPortion.id,
                      name: mappedPortion.serving_size_option_name || item.portion_name || '',
                      serving_type_name: menuItem?.serving_type_detail?.name || '',
                      price: parseFloat(mappedPortion.price ?? item.unit_price ?? 0),
                    }
                  : (item.portion_name
                      ? {
                          id: item.menu_item_portion || null,
                          name: item.portion_name,
                          serving_type_name: menuItem?.serving_type_detail?.name || '',
                          price: parseFloat(item.unit_price || 0),
                        }
                      : null),
                item.recipe_ingredients_snapshot || [],
                item.quantity || 1
              );
            }
          });
          if (parseFloat(order.discount_percent) > 0) {
            setDiscount(parseFloat(order.discount_percent), 0);
          } else if (parseFloat(order.discount_amount) > 0) {
            setDiscount(0, parseFloat(order.discount_amount));
          }
          setNotes(order.notes || '');
          setEditingOrderId(order.id);
          toast.success(`Loaded order ${order.order_number} for editing`);
        } catch (error) {
          toast.error('Failed to load order for editing');
        }
      };
      loadOrder();
    }
  }, [searchParams, menuItems]);

  const fetchData = async () => {
    try {
      const [catRes, menuRes, settingsRes] = await Promise.all([
        api.get('/categories/?active=true&page_size=500'),
        api.get('/menu-items/?available=true&page_size=500'),
        api.get('/settings/')
      ]);
      const cats = catRes.data.results || catRes.data;
      const items = menuRes.data.results || menuRes.data;
      const sett = settingsRes.data;
      setCategories(cats);
      setMenuItems(items);
      setSettings(sett);
      // Load the most recent till cash-count time (for the forced-count interval check).
      if (parseInt(sett?.cash_count_interval_minutes || 0, 10) > 0) {
        api.get('/till-cash-counts/?page_size=1')
          .then((r) => { const rows = r.data.results || r.data || []; if (rows.length) setLastCashCountAt(rows[0].counted_at); })
          .catch(() => {});
      }
      cacheCategories(cats);
      cacheMenuItems(items);
      cacheSettings(sett);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (cachedMenuItems.length > 0) {
        setCategories(cachedCategories);
        setMenuItems(cachedMenuItems);
        setSettings(cachedSettings);
        toast('Using cached menu data (offline mode)', { icon: '📴' });
      } else {
        toast.error('Failed to load menu data');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = !selectedCategory ||
      (selectedCategory === 'fav' ? item.is_favorite : item.category === selectedCategory);
    const matchesSearch = !searchQuery ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || String(a.name || '').localeCompare(String(b.name || '')));

  const handleHoldSale = async () => {
    if (items.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    try {
      const orderData = getOrderData('cash', 0);
      await api.post('/orders/hold_order/', orderData);
      clearCart();
      toast.success('Sale held! You can resume it from the Sales page.');
    } catch (error) {
      console.error('Error holding sale:', error);
      toast.error('Failed to hold sale');
    }
  };

  const isCashCountDue = () => {
    const interval = parseInt(settings?.cash_count_interval_minutes || 0, 10);
    if (!interval || interval <= 0) return false;
    if (!lastCashCountAt) return true; // never counted → due on the first punch
    const gapMin = (Date.now() - new Date(lastCashCountAt).getTime()) / 60000;
    return gapMin > interval;
  };

  const saveCashCount = async () => {
    const amt = parseFloat(cashCountAmount);
    if (Number.isNaN(amt) || amt < 0) { toast.error('Enter the total cash in the till'); return; }
    setSavingCashCount(true);
    try {
      const res = await api.post('/till-cash-counts/', { amount: amt, counted_at: new Date().toISOString() });
      setLastCashCountAt(res.data?.counted_at || new Date().toISOString());
      setShowCashCountModal(false);
      setCashCountAmount('');
      toast.success('Till cash recorded. You can complete the sale now.');
    } catch (e) {
      toast.error('Failed to record till cash count');
    } finally {
      setSavingCashCount(false);
    }
  };

  const completePayment = async (selectedMethod = paymentMethod, selectedAmountReceived = amountReceived) => {
    if (items.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    // Forced till cash-count: if due, block this punch until the cash is recorded (real sales only).
    if (!isBackdateMode && isCashCountDue()) {
      setShowCashCountModal(true);
      return;
    }

    const total = getFinalTotal();
    const received = selectedMethod === 'cash'
      ? (parseFloat(selectedAmountReceived) || total)
      : total;

    if (selectedMethod === 'cash' && received < total) {
      toast.error('Insufficient amount received');
      return;
    }

    // --- Backdate mode: post to add_previous_sale, stay on page for next entry ---
    if (isBackdateMode) {
      if (!backdateDate) { toast.error('Select a date for this previous sale'); return; }
      try {
        const orderData = getOrderData(selectedMethod, received);
        const res = await api.post('/orders/add_previous_sale/', {
          ...orderData,
          sale_date: backdateDate,
          sale_time: backdateTime,
        });
        localStorage.setItem('pos_backdate_date', backdateDate);
        localStorage.setItem('pos_backdate_time', backdateTime);
        const count = backdateSaleCount + 1;
        setBackdateSaleCount(count);
        const cartSnapshot = [...items];
        clearCart();
        setAmountReceived('');
        setPaymentProofFile(null);
        setShowPayment(false);
        setAddedBackdateSales(prev => [...prev, {
          orderNumber: res.data.order_number,
          total: res.data.total_amount,
          itemCount: cartSnapshot.reduce((s, i) => s + Number(i.quantity || 0), 0),
          date: backdateDate,
          time: backdateTime,
          itemSummary: cartSnapshot.map(i => `${i.quantity}× ${getItemDisplayName(i)}`).join(', '),
          paymentMethod: selectedMethod,
        }]);
        if (res.data.inventory_warnings?.length) {
          res.data.inventory_warnings.forEach(w => toast(w, { icon: '⚠️' }));
        }
        toast.success(`Sale added: ${res.data.order_number}`);
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to add previous sale');
      }
      return;
    }

    try {
      const orderData = getOrderData(selectedMethod, received);

      // If editing a pending order: cancel old pending + create new order with updated cart
      if (editingOrderId) {
        // Cancel the old pending order (no inventory reversal needed since pending orders don't deduct)
        await api.post(`/orders/${editingOrderId}/cancel/`);
        // Create fresh order with updated items from cart
        const response = await api.post('/orders/create_order/', orderData);

        // Upload payment proof if present
        if (paymentProofFile && response.data?.id) {
          try {
            const payments = response.data.payments || [];
            const digitalPayment = payments.find(p => p.payment_type !== 'cash');
            if (digitalPayment) {
              const fd = new FormData();
              fd.append('model_type', 'payment');
              fd.append('object_id', digitalPayment.id);
              fd.append('image', paymentProofFile);
              await api.post('/upload-image/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
          } catch (uploadErr) {
            console.error('Failed to upload payment proof:', uploadErr);
          }
        }

        setLastOrder(response.data);
        setShowPayment(false);
        clearCart();
        setAmountReceived('');
        setPaymentProofFile(null);
        setEditingOrderId(null);
        toast.success('Order updated and completed!');
        navigate('/pos', { replace: true });
        return;
      }

      if (!isOnline) {
        const offlineOrder = addPendingOrder(orderData);
        setLastOrder({ ...orderData, order_number: offlineOrder.id, status: 'pending_sync', created_at: new Date().toISOString(), items: items.map(i => ({ menu_item_name: getItemDisplayName(i), quantity: i.quantity, total_price: i.quantity * i.price })), subtotal: getSubtotal(), discount_amount: getDiscountValue(), tax_amount: getTaxAmount(), total_amount: total, amount_received: received, change_amount: Math.max(0, received - total), payment_method: selectedMethod });
        setShowPayment(false);
        clearCart();
        setAmountReceived('');
        toast.success('Order saved offline. Will sync when back online.', { icon: '📴' });
        return;
      }

      const response = await api.post('/orders/create_order/', orderData);
      
      // Upload payment proof for digital/card payments
      if (paymentProofFile && response.data?.id) {
        try {
          const payments = response.data.payments || [];
          const digitalPayment = payments.find(p => p.payment_type !== 'cash');
          if (digitalPayment) {
            const fd = new FormData();
            fd.append('model_type', 'payment');
            fd.append('object_id', digitalPayment.id);
            fd.append('image', paymentProofFile);
            await api.post('/upload-image/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          }
        } catch (uploadErr) {
          console.error('Failed to upload payment proof:', uploadErr);
        }
      }

      setLastOrder(response.data);
      setShowPayment(false);
      clearCart();
      setAmountReceived('');
      setPaymentProofFile(null);
      toast.success('Order completed successfully!');
    } catch (error) {
      console.error('Error creating order:', error);
      if (!isOnline) {
        const orderData = getOrderData(selectedMethod, received);
        const offlineOrder = addPendingOrder(orderData);
        setLastOrder({ ...orderData, order_number: offlineOrder.id, status: 'pending_sync', created_at: new Date().toISOString(), items: items.map(i => ({ menu_item_name: getItemDisplayName(i), quantity: i.quantity, total_price: i.quantity * i.price })), subtotal: getSubtotal(), discount_amount: getDiscountValue(), tax_amount: getTaxAmount(), total_amount: total, amount_received: received, change_amount: Math.max(0, received - total), payment_method: selectedMethod });
        setShowPayment(false);
        clearCart();
        setAmountReceived('');
        toast.success('Order saved offline. Will sync when back online.', { icon: '📴' });
      } else {
        toast.error('Failed to complete order');
      }
    }
  };

  const handlePayment = async () => {
    await completePayment(paymentMethod, amountReceived);
  };

  const handleQuickPayment = async (selectedMethod) => {
    if (selectedMethod === 'cash') {
      await completePayment('cash', String(getFinalTotal()));
      return;
    }
    await completePayment(selectedMethod, String(getFinalTotal()));
  };

  const openRecentSales = async () => {
    setShowRecentSales(true);
    setRecentSalesLoading(true);
    try {
      if (isBackdateMode && backdateDate) {
        const res = await api.get(`/orders/?start_date=${backdateDate}&end_date=${backdateDate}&page_size=10`);
        setRecentSales((res.data.results || res.data || []).slice(0, 10));
      } else {
        const res = await api.get('/dashboard/stats/?period=today');
        setRecentSales(res.data.recent_orders || []);
      }
    } catch (e) {
      setRecentSales([]);
    } finally {
      setRecentSalesLoading(false);
    }
  };

  const viewHistoricalOrder = async (order) => {
    setShowRecentSales(false);
    try {
      const res = await api.get(`/orders/${order.id}/receipt/`);
      setHistoricalOrder(res.data.order || order);
    } catch (e) {
      setHistoricalOrder(order);
    }
  };

  const applyDiscount = () => {
    const value = parseFloat(discountValue) || 0;
    if (discountType === 'percent') {
      setDiscount(value, 0);
    } else {
      setDiscount(0, value);
    }
    setNotes(discountNote.trim());
    setShowDiscount(false);
    setDiscountValue('');
    toast.success('Discount applied');
  };

  const getTaxAmount = () => {
    if (settings?.tax_rate > 0) {
      return getTotal() * parseFloat(settings.tax_rate) / 100;
    }
    return 0;
  };

  const getFinalTotal = () => {
    return getTotal() + getTaxAmount();
  };

  const getMenuFallbackIcon = () => UtensilsCrossed;

  const formatCurrency = (amount) => {
    return formatUiCurrencyDetailed(amount, settings?.currency_symbol || 'Rs.');
  };

  const getPortionConfigForItem = (menuItem, portionId, portionName = '') => {
    const portionConfigs = menuItem?.portion_configs || [];
    return portionConfigs.find(config => String(config.id) === String(portionId))
      || portionConfigs.find(config => String(config.serving_size_option_name || '').trim().toLowerCase() === String(portionName || '').trim().toLowerCase())
      || null;
  };

  const menuItemRequiresConfiguration = (menuItem) => (
    Boolean((menuItem?.portion_configs || []).length > 0)
    || Boolean(menuItem?.has_modifiers && (menuItem?.modifiers || []).length > 0)
  );

  const handleMenuItemClick = (menuItem) => {
    if (menuItemRequiresConfiguration(menuItem)) {
      setSelectedMenuItem(menuItem);
      setEditingCartItem(null);
      setShowModifierSelection(true);
      return;
    }
    addItem(menuItem);
  };

  // Direct-punch a specific portion from the card shortcut buttons.
  // If the item also has modifiers, fall back to the popup (with portion pre-noted).
  const handlePortionDirectAdd = (e, menuItem, portionConfig) => {
    e.stopPropagation();
    const hasModifiers = menuItem?.has_modifiers && (menuItem?.modifiers || []).length > 0;
    if (hasModifiers) {
      // Open modifier popup — ModifierSelection will pre-select this portion
      setSelectedMenuItem({ ...menuItem, _preselectedPortionId: portionConfig.id });
      setEditingCartItem(null);
      setShowModifierSelection(true);
      return;
    }
    // No modifiers — add directly
    const portionData = {
      id: portionConfig.id,
      name: portionConfig.serving_size_option_name || '',
      serving_type_name: menuItem?.serving_type_detail?.name || '',
      price: parseFloat(portionConfig.price ?? menuItem?.selling_price ?? 0),
    };
    addItem(menuItem, [], 0, menuItem.base_components || [], portionData, [], 1);
  };

  // Get first letter of the serving size name (e.g. "Regular" → "R", "Large" → "L")
  const getPortionShortLabel = (portion) => {
    const name = String(portion?.serving_size_option_name || '').trim();
    return name ? name[0].toUpperCase() : '?';
  };

  const handleModifierConfirm = ({ modifier_selections, base_components, price_adjustment, portion_id = null, portion_name = '', serving_type_name = '', base_price = null, recipe_ingredients_snapshot = [], quantity = 1 }) => {
    const portionConfig = portion_id
      ? getPortionConfigForItem(selectedMenuItem, portion_id, portion_name)
      : null;
    const portionData = portionConfig
      ? {
          id: portionConfig.id,
          name: portionConfig.serving_size_option_name || portion_name,
          serving_type_name: selectedMenuItem?.serving_type_detail?.name || serving_type_name,
          price: parseFloat(portionConfig.price ?? base_price ?? selectedMenuItem?.selling_price ?? 0),
        }
      : (portion_name
          ? {
              id: portion_id,
              name: portion_name,
              serving_type_name,
              price: parseFloat(base_price ?? selectedMenuItem?.selling_price ?? 0),
            }
          : null);

    if (editingCartItem) {
      removeItem(
        editingCartItem.menu_item_id,
        editingCartItem.modifier_selections || [],
        editingCartItem.base_components || [],
        editingCartItem.portion_id || null
      );
      addItem(
        selectedMenuItem,
        modifier_selections,
        price_adjustment,
        base_components,
        portionData,
        recipe_ingredients_snapshot,
        quantity
      );
    } else {
      addItem(selectedMenuItem, modifier_selections, price_adjustment, base_components, portionData, recipe_ingredients_snapshot, quantity);
    }
    setShowModifierSelection(false);
    setSelectedMenuItem(null);
    setEditingCartItem(null);
  };

  const handleEditModifiers = (cartItem) => {
    const menuItem = menuItems.find(item => item.id === cartItem.menu_item_id);
    if (!menuItem || !menuItemRequiresConfiguration(menuItem)) {
      return;
    }
    setSelectedMenuItem(menuItem);
    setEditingCartItem(cartItem);
    setShowModifierSelection(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col lg:flex-row gap-4 lg:gap-6 h-auto lg:h-[calc(100vh-140px)] ${isBackdateMode ? (addedBackdateSales.length > 0 ? 'pt-64' : 'pt-24') : ''}`}>

      {/* Backdate Mode Banner */}
      {isBackdateMode && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-orange-600 text-white shadow-lg">
          {/* Row 1: Title + counters + Done */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-orange-500">
            <div className="flex items-center gap-2 min-w-0">
              <Clock size={16} className="flex-shrink-0" />
              <span className="font-semibold text-sm">Adding Previous Sale</span>
              {backdateSaleCount > 0 && (
                <span className="bg-orange-800 text-white text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                  {backdateSaleCount} added
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/orders')}
              className="flex items-center gap-1 bg-orange-800 hover:bg-orange-900 text-white text-sm font-bold px-3 py-1.5 rounded-lg transition flex-shrink-0 ml-2"
            >
              Done
            </button>
          </div>
          {/* Row 2: Date + Time pickers — stacked on mobile, inline on desktop */}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
            <div className="flex-1 min-w-[140px] max-w-[200px]">
              <UiDateField
                value={backdateDate}
                onChange={setBackdateDate}
                max={todayIso}
                placeholder="Pick date"
              />
            </div>
            <div className="w-28 flex-shrink-0">
              <UiTimeField value={backdateTime} onChange={setBackdateTime} />
            </div>
            {backdateDate && (
              <span className="text-orange-200 text-xs hidden sm:block">
                Orders for {backdateDate} {backdateTime}
              </span>
            )}
          </div>
          {/* Added sales list */}
          {addedBackdateSales.length > 0 && (
            <div className="border-t border-orange-500 px-3 py-2 max-h-40 overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-orange-200 font-medium">Sales added this session:</p>
                <p className="text-xs font-bold text-white">
                  {addedBackdateSales.length} order{addedBackdateSales.length !== 1 ? 's' : ''} · {addedBackdateSales.reduce((s, x) => s + Number(x.itemCount || 0), 0)} items · Rs.{addedBackdateSales.reduce((s, x) => s + Number(x.total || 0), 0).toLocaleString()}
                </p>
              </div>
              <div className="space-y-1">
                {addedBackdateSales.map((sale, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs bg-orange-700 rounded px-2 py-1">
                    <div className="min-w-0 flex-1 truncate">
                      <span className="font-semibold">{sale.orderNumber}</span>
                      <span className="text-orange-200 ml-2">{sale.date} {sale.time}</span>
                      <span className="text-orange-300 ml-2 hidden sm:inline truncate">{sale.itemSummary}</span>
                    </div>
                    <span className="font-bold ml-2 flex-shrink-0">Rs.{Number(sale.total || 0).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/orders')}
                className="mt-2 w-full py-1.5 bg-orange-800 hover:bg-orange-900 text-white text-sm font-bold rounded-lg transition"
              >
                Done — Go to Orders
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile Cart Toggle — fixed bottom-right, opens the cart */}
      <button
        onClick={() => setShowMobileCart(true)}
        className="lg:hidden fixed z-30 bottom-4 right-4 bg-primary-600 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2"
      >
        <Receipt size={20} />
        <span className="font-medium">{getItemCount()}</span>
        <span className="font-bold">{formatCurrency(getFinalTotal())}</span>
      </button>

      {/* Menu Section */}
      <div className="flex-1 flex flex-col min-h-[60vh] lg:min-h-0 min-w-0">
        {/* Mobile Cart Summary - Always visible */}
          <UiPanel className="mb-3 lg:hidden" bodyClassName="p-3">
            {items.length > 0 ? (
              <>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-gray-500">
                      <th className="text-left py-1.5">#</th>
                      <th className="text-left py-1.5">Item</th>
                      <th className="text-center py-1.5">Qty</th>
                      <th className="text-right py-1.5">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={`${item.menu_item_id}-${item.portion_id || 'default'}-${JSON.stringify(item.modifier_selections || [])}-${JSON.stringify(item.base_components || [])}`} className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-400">{idx + 1}</td>
                        <td className="py-1.5 text-gray-800 truncate max-w-[140px]">{getItemDisplayName(item)}</td>
                        <td className="py-1.5 text-center text-gray-600">{item.quantity}</td>
                        <td className="py-1.5 text-right font-medium text-primary-600">{formatCurrency(item.price * item.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t">
                <div>
                  <span className="text-xs text-gray-500">{getItemCount()} items</span>
                  <span className="font-bold text-sm ml-2">{formatCurrency(getFinalTotal())}</span>
                </div>
                <div className="flex items-center gap-2">
                    <UiButton
                      onClick={() => handleQuickPayment('cash')}
                      variant="success"
                      size="sm"
                      icon={Banknote}
                      className="rounded-lg"
                    >
                      Pay Cash
                    </UiButton>
                    <UiButton
                      onClick={() => handleQuickPayment('digital_wallet')}
                      variant="info"
                      size="sm"
                      icon={Smartphone}
                      className="rounded-lg"
                    >
                      Pay Digital
                    </UiButton>
                    <UiButton
                      onClick={() => setShowPayment(true)}
                      variant="primary"
                      size="sm"
                      icon={CreditCard}
                      className="rounded-lg"
                    >
                      Pay
                    </UiButton>
                  </div>
                </div>
              </>
            ) : (
              <UiEmptyState icon={ShoppingCart} title="Cart is empty" subtitle="Tap items below to add to cart" className="py-4" />
            )}
          </UiPanel>

          {/* Search & Categories */}
          <UiPanel className="mb-4" bodyClassName="p-4">
            <UiSearchField
              className="mb-4"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menu items..."
            />
            <div className="flex gap-2 overflow-x-auto pb-2">
              <UiFilterChip
                onClick={() => setSelectedCategory(null)}
                active={!selectedCategory}
                className="min-w-[68px] flex-shrink-0"
              >
                <ShoppingCart size={28} />
                <span className="text-sm">All</span>
              </UiFilterChip>
              <UiFilterChip
                onClick={() => setSelectedCategory('fav')}
                active={selectedCategory === 'fav'}
                activeClassName="bg-yellow-500 text-white"
                inactiveClassName="bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200"
                className="min-w-[68px] flex-shrink-0"
                title="Favorites"
              >
                <Star size={28} fill={selectedCategory === 'fav' ? 'white' : 'currentColor'} />
                <span className="text-sm">Fav</span>
              </UiFilterChip>
              {categories.map((cat) => {
                const CatIcon = getMenuFallbackIcon();
                return (
                  <UiFilterChip
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    active={selectedCategory === cat.id}
                    className="min-w-[68px] flex-shrink-0"
                    title={cat.name}
                  >
                    {cat.image ? (
                      <img src={cat.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                    ) : (
                      <CatIcon size={28} />
                    )}
                    <span className="hidden sm:inline text-sm">{cat.name}</span>
                  </UiFilterChip>
                );
              })}
            </div>
          </UiPanel>

        {/* Menu Items Grid */}
          <UiPanel className="flex-1 overflow-hidden" bodyClassName="h-full overflow-auto p-3 sm:p-4">
            <div className="pos-grid">
              {filteredItems.map((item) => {
              const ItemIcon = getMenuFallbackIcon();
              const portions = item?.portion_configs || [];
              const hasPortions = portions.length > 0;
              return (
              <div
                key={item.id}
                className="bg-gradient-to-br from-primary-50 to-white border-2 border-primary-100 rounded-xl text-center hover:border-primary-400 hover:shadow-lg transition-all group flex flex-col relative"
              >
                {/* Main card tap area — opens popup for details/modifiers */}
                <button
                  onClick={() => handleMenuItemClick(item)}
                  className="flex-1 flex flex-col items-center p-4 pb-2 w-full"
                >
                  {item.has_modifiers && (item.modifiers || []).length > 0 && !hasPortions && (
                    <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs px-2 py-1 rounded-full">
                      Customize
                    </div>
                  )}
                  <div className="mb-2 flex justify-center">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-contain" />
                    ) : (
                      <ItemIcon size={28} className="text-primary-500" />
                    )}
                  </div>
                  <p className="font-medium text-gray-800 text-sm leading-tight mb-1 group-hover:text-primary-700">
                    {item.name}
                  </p>
                  <p className="text-primary-600 font-bold text-sm">
                    {formatCurrency(item.selling_price)}
                  </p>
                </button>

                {/* Portion shortcut buttons — shown at bottom of card */}
                {hasPortions && (
                  <div className="flex border-t border-primary-100 divide-x divide-primary-100 rounded-b-xl overflow-hidden">
                    {portions.slice(0, 5).map((portion) => (
                      <button
                        key={portion.id}
                        onClick={(e) => handlePortionDirectAdd(e, item, portion)}
                        title={`${portion.serving_size_option_name} — ${formatCurrency(portion.price ?? item.selling_price)}`}
                        className="flex-1 py-1.5 text-xs font-bold text-primary-700 hover:bg-primary-600 hover:text-white transition-colors"
                      >
                        {getPortionShortLabel(portion)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
                );
              })}
            </div>
            {filteredItems.length === 0 && (
              <UiEmptyState title="No items found" subtitle="Try another search or menu group." />
            )}
          </UiPanel>
        </div>

      {/* Mobile Cart Overlay */}
      {showMobileCart && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setShowMobileCart(false)} />
      )}

      {/* Cart Section */}
      <div className={`
        lg:w-96 lg:relative lg:translate-x-0 lg:block lg:z-0
        fixed inset-y-0 right-0 w-full sm:w-96 z-50
        transform transition-transform duration-300
        ${showMobileCart ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        bg-white rounded-l-xl lg:rounded-xl shadow-sm flex flex-col
      `}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <button 
                onClick={() => setShowMobileCart(false)}
                className="lg:hidden p-1 hover:bg-gray-100 rounded-lg mr-2"
              >
              <X size={20} />
            </button>
              Current Order
            </h2>
            <UiButton onClick={openRecentSales} variant="ghost" size="sm" icon={Clock} className="rounded-lg px-3 py-1.5">
              Recent
            </UiButton>
          </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-auto p-4">
            {items.length === 0 ? (
              <UiEmptyState icon={Receipt} title="Cart is empty" subtitle="Add items from the menu" />
            ) : (
            <div className="space-y-3">
              {items.map((item) => (
                <div key={`${item.menu_item_id}-${item.portion_id || 'default'}-${JSON.stringify(item.modifier_selections || [])}-${JSON.stringify(item.base_components || [])}`} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{getItemDisplayName(item)}</p>
                      <p className="text-sm text-gray-500">{formatCurrency(item.price)} each</p>
                      {item.modifier_selections?.length > 0 && (
                        <button onClick={() => handleEditModifiers(item)} className="text-xs text-primary-600 mt-1">Edit selections</button>
                      )}
                    </div>
                    <button
                      onClick={() => removeItem(item.menu_item_id, item.modifier_selections || [], item.base_components || [], item.portion_id || null)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1, item.modifier_selections || [], item.base_components || [], item.portion_id || null)}
                        className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1, item.modifier_selections || [], item.base_components || [], item.portion_id || null)}
                        className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-100"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <p className="font-bold text-primary-600">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Footer */}
        <div className="border-t border-gray-100 p-4 space-y-3">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>{formatCurrency(getSubtotal())}</span>
          </div>
          {getDiscountValue() > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount {discountPercent > 0 ? `(${discountPercent}%)` : ''}</span>
              <span>-{formatCurrency(getDiscountValue())}</span>
            </div>
          )}
          {getTaxAmount() > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Tax ({parseFloat(settings.tax_rate)}%)</span>
              <span>{formatCurrency(getTaxAmount())}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t">
            <span>Total</span>
            <span className="text-primary-600">{formatCurrency(getFinalTotal())}</span>
          </div>

            <div className="flex gap-2">
              <UiButton
                onClick={() => { setDiscountNote(notes || ''); setShowDiscount(true); }}
                disabled={items.length === 0}
                variant="outlinePrimary"
                size="lg"
                icon={Percent}
                className="flex-1 rounded-lg"
              >
                Discount
              </UiButton>
              <UiButton
                onClick={handleHoldSale}
                disabled={items.length === 0}
                variant="warning"
                size="lg"
                icon={Receipt}
                className="rounded-lg px-4"
                title="Hold sale for later"
              >
                Hold
              </UiButton>
              <UiButton
                onClick={clearCart}
                disabled={items.length === 0}
                variant="danger"
                size="lg"
                icon={Trash2}
                className="rounded-lg px-4"
              >
                <span className="sr-only">Clear order</span>
              </UiButton>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <UiButton
                onClick={() => handleQuickPayment('cash')}
                disabled={items.length === 0}
                variant="success"
                size="lg"
                icon={Banknote}
                className="py-4 text-sm"
              >
                Pay Cash
              </UiButton>
              <UiButton
                onClick={() => handleQuickPayment('digital_wallet')}
                disabled={items.length === 0}
                variant="info"
                size="lg"
                icon={Smartphone}
                className="py-4 text-sm"
              >
                Pay Digital
              </UiButton>
              <UiButton
                onClick={() => setShowPayment(true)}
                disabled={items.length === 0}
                variant="primary"
                size="lg"
                icon={CreditCard}
                className="py-4 text-lg"
              >
                Pay
              </UiButton>
            </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPayment && (
        <UiModalShell title="Payment" onClose={() => setShowPayment(false)} className="max-w-md" contentClassName="p-4 sm:p-6">
            <div className="space-y-4">
              <div className="bg-primary-50 rounded-xl p-4 text-center">
                <p className="text-sm text-primary-600 mb-1">Total Amount</p>
                <p className="text-3xl font-bold text-primary-700">{formatCurrency(getFinalTotal())}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'cash', icon: Banknote, label: 'Cash' },
                    { value: 'card', icon: CreditCard, label: 'Card' },
                    { value: 'digital_wallet', icon: Smartphone, label: 'Digital' },
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setPaymentMethod(method.value)}
                      className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${
                        paymentMethod === method.value
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <method.icon size={24} />
                      <span className="text-sm font-medium">{method.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {paymentMethod === 'cash' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount Received</label>
                  <input
                    type="number"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    placeholder={getFinalTotal().toFixed(2)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg"
                  />
                  {amountReceived && parseFloat(amountReceived) >= getFinalTotal() && (
                    <p className="mt-2 text-green-600 font-medium">
                      Change: {formatCurrency(parseFloat(amountReceived) - getFinalTotal())}
                    </p>
                  )}
                </div>
              )}

              {(paymentMethod === 'digital_wallet' || paymentMethod === 'card') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Proof (Optional)</label>
                  <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition text-sm text-gray-600">
                    <Receipt size={18} className="text-primary-500" />
                    {paymentProofFile ? paymentProofFile.name : 'Upload screenshot / receipt'}
                    <input type="file" accept="image/*" onChange={(e) => setPaymentProofFile(e.target.files[0])} className="hidden" />
                  </label>
                </div>
              )}

              <button
                onClick={handlePayment}
                className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg transition"
              >
                Complete Payment
              </button>
            </div>
        </UiModalShell>
      )}

      {/* Discount Modal */}
      {showDiscount && (
        <UiModalShell title="Apply Discount" onClose={() => setShowDiscount(false)} className="max-w-md" contentClassName="p-4 sm:p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setDiscountType('percent')}
                  className={`p-3 rounded-lg border-2 font-medium transition ${
                    discountType === 'percent'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200'
                  }`}
                >
                  Percentage (%)
                </button>
                <button
                  onClick={() => setDiscountType('fixed')}
                  className={`p-3 rounded-lg border-2 font-medium transition ${
                    discountType === 'fixed'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200'
                  }`}
                >
                  Fixed Amount
                </button>
              </div>

              <input
                type="number"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percent' ? 'Enter percentage' : 'Enter amount'}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount note (optional)</label>
                <textarea
                  value={discountNote}
                  onChange={(e) => setDiscountNote(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Reason, customer, approval, or reference"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              <button
                onClick={applyDiscount}
                className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
              >
                Apply Discount
              </button>
            </div>
        </UiModalShell>
      )}

      {/* Historical Order Receipt (same modal as Sales page) */}
      {historicalOrder && (
        <OrderReceiptModal
          order={historicalOrder}
          settings={settings}
          onClose={() => setHistoricalOrder(null)}
        />
      )}

      {/* Recent Sales Panel */}
      {showRecentSales && (
        <UiModalShell title={isBackdateMode && backdateDate ? `Sales on ${backdateDate}` : 'Recent Sales'} onClose={() => setShowRecentSales(false)} className="max-w-lg" contentClassName="max-h-[75vh] overflow-y-auto p-4 space-y-2">
              {recentSalesLoading && <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" /></div>}
              {!recentSalesLoading && recentSales.map(order => (
                <div key={order.id} onClick={() => viewHistoricalOrder(order)} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-primary-50 transition gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-gray-800 truncate">{(order.items || []).map(i => `${i.quantity}× ${i.menu_item_name}`).join(', ') || '—'}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{order.order_number} · {formatUiDateTime(order.created_at)}</p>
                  </div>
                  <p className="font-bold text-primary-600 flex-shrink-0">{formatCurrency(order.total_amount)}</p>
                </div>
              ))}
              {!recentSalesLoading && recentSales.length === 0 && <p className="text-center text-gray-500 py-8">No recent sales</p>}
        </UiModalShell>
      )}

      {/* Receipt Modal */}
      {showReceipt && lastOrder && (
        <OrderReceiptModal
          order={lastOrder}
          settings={settings}
          onClose={() => setShowReceipt(false)}
        />
      )}

      <ModifierSelection
        isOpen={showModifierSelection}
        onClose={() => {
          setShowModifierSelection(false);
          setSelectedMenuItem(null);
          setEditingCartItem(null);
        }}
        onConfirm={handleModifierConfirm}
        menuItem={selectedMenuItem}
        initialData={editingCartItem}
      />

      {showCashCountModal && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-1">Count the Till</h3>
            <p className="text-sm text-gray-500 mb-3">Time for a cash check. Enter the total cash currently in the till to continue.</p>
            <input
              type="number" autoFocus min="0" step="0.01"
              value={cashCountAmount}
              onChange={(e) => setCashCountAmount(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCashCount(); }}
              placeholder="Total cash in till"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg mb-3 text-lg font-semibold"
            />
            <div className="flex gap-2">
              <button onClick={() => { setShowCashCountModal(false); setCashCountAmount(''); }} className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium">Cancel</button>
              <button onClick={saveCashCount} disabled={savingCashCount} className="flex-1 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{savingCashCount ? 'Saving…' : 'Record & Continue'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POS;
